import { useEffect } from 'react'
import { PageHead } from '@components/PageHead'
import { consumePendingPassword } from '@features/tools/services/generatorHandoff'
import { VaultItemFormView } from '@features/vault/components/VaultItemFormView'
import { useFolders } from '@features/vault/hooks/useFolders'
import { useVaultItemForm } from '@features/vault/hooks/useVaultItemForm'
import { Icon } from '@/icons/Icon'
import { useTranslation } from '@/i18n'

/**
 * 新規 Vault アイテム作成画面 (T-D-Vault-B、xlsx No.28)。
 *
 * Form 描画は `VaultItemFormView` に集約 (F4 で抽出)。本 page は PageHead +
 * 「保管庫に戻る」+ form card 下のアクションボタン (キャンセル / 保存) を担当する。
 *
 * 編集は `VaultEditPage` (F4) を参照。
 */

const FORM_ID = 'vault-new-form'

const goVault = (): void => {
  window.location.hash = 'vault'
}

export const VaultNewPage = (): JSX.Element => {
  const { t } = useTranslation()
  const form = useVaultItemForm()
  const { folders } = useFolders()

  // Generator → VaultNewPage の handoff (`#tools/generator` で生成した
  // パスワードを mount 時に拾う、Feature 2 bug fix)。
  useEffect(() => {
    const pending = consumePendingPassword()

    if (pending !== null) {
      form.setType('login')
      form.setLogin('password', pending)
    }
    // mount 1 回のみ。form は stable な hook なので依存配列空で OK
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div>
      <PageHead
        title={t('vault.new.title')}
        sub={t('vault.new.subtitle')}
        actions={
          <button type="button" className="btn btn-secondary" onClick={goVault}>
            <Icon name="arrow-left" aria-hidden="true" />
            {t('vault.new.back')}
          </button>
        }
      />
      <div className="page-body">
        <VaultItemFormView
          form={form}
          folders={folders}
          formId={FORM_ID}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={goVault}>
                {t('vault.new.cancel')}
              </button>
              <button
                type="submit"
                form={FORM_ID}
                className="btn btn-primary"
                disabled={form.isSubmitting}
              >
                <Icon name="save" aria-hidden="true" />
                {form.isSubmitting ? t('vault.new.saving') : t('vault.new.save')}
              </button>
            </>
          }
        />
      </div>
    </div>
  )
}
